import React from 'react';
import { Box, Grid, Skeleton, List, ListItem, Divider } from '@mui/material';

/**
 * ArticleSkeleton - Componente de loading skeleton para páginas de artigos
 * 
 * Estrutura:
 * - Breadcrumbs no topo
 * - Seções/navegação na esquerda
 * - Conteúdo principal no centro
 * 
 * @param {Object} props
 * @param {boolean} props.showSections - Exibe a seção de navegação lateral (padrão: true)
 * @param {number} props.sectionsCount - Quantidade de seções skeleton a exibir (padrão: 4)
 * @param {number} props.articlesPerSection - Quantidade de artigos por seção (padrão: 2)
 * @param {number} props.contentParagraphs - Quantidade de parágrafos no conteúdo (padrão: 5)
 * @param {string} props.animation - Tipo de animação: 'pulse' | 'wave' | false (padrão: 'wave')
 */
const ArticleMainContentSkeleton = ({
    showSections = true,
    showBreadcrumbs = true,
    showTags = true,
    showFeedback = false,
    sectionsCount = 4,
    articlesPerSection = 2,
    contentParagraphs = 5,
    animation = 'wave',
    loading = true,
    mainContainerProps = {}
}) => {
    // Renderiza os breadcrumbs skeleton
    const renderBreadcrumbsSkeleton = () => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Skeleton animation={animation} variant="text" width={140} height={24} />
            <Skeleton animation={animation} variant="circular" width={16} height={16} />
            <Skeleton animation={animation} variant="text" width={180} height={24} />
        </Box>
    );

    // Renderiza uma seção com artigos colapsáveis skeleton
    const renderSectionSkeleton = (sectionIndex) => (
        <Box key={sectionIndex} sx={{ mb: 1, width: '100%' }}>
            {/* Título da seção */}
            <ListItem
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1.5,
                    px: 2,
                    borderRadius: 1,
                    '&:hover': { backgroundColor: 'action.hover' }
                }}
            >
                <Skeleton
                    animation={animation}
                    variant="text"
                    width={`${60 + Math.random() * 30}%`}
                    height={22}
                />
                <Skeleton animation={animation} variant="circular" width={20} height={20} />
            </ListItem>

            {/* Artigos da seção (quando expandido) */}
            {sectionIndex === 1 && ( // Simular uma seção aberta
                <Box sx={{ pl: 2 }}>
                    {Array.from({ length: articlesPerSection }).map((_, articleIndex) => (
                        <ListItem
                            key={articleIndex}
                            sx={{
                                py: 1,
                                pl: 3,
                                borderRadius: 1,
                            }}
                        >
                            <Skeleton
                                animation={animation}
                                variant="text"
                                width={`${50 + Math.random() * 40}%`}
                                height={20}
                            />
                        </ListItem>
                    ))}
                </Box>
            )}
        </Box>
    );

    // Renderiza a navegação lateral de seções
    const renderSectionsSkeleton = () => (
        <List sx={{ width: '100%' }} component="nav">
            {Array.from({ length: sectionsCount }).map((_, index) => renderSectionSkeleton(index))}
        </List>
    );

    // Renderiza o conteúdo principal skeleton
    const renderContentSkeleton = () => (
        <Box sx={{ width: '100%' }}>
            {/* Título do artigo */}
            <Skeleton
                animation={animation}
                variant="text"
                width="70%"
                height={48}
                sx={{ mb: 1 }}
            />

            {/* Subtítulo */}
            <Skeleton
                animation={animation}
                variant="text"
                width="90%"
                height={28}
                sx={{ mb: 2 }}
            />

            <Divider sx={{ mb: 3 }} />

            {/* Informações do card (ex: "4088 | CMTech") */}
            <Box sx={{ mb: 3 }}>
                <Skeleton
                    animation={animation}
                    variant="text"
                    width={180}
                    height={32}
                    sx={{ mb: 1 }}
                />
                <Skeleton
                    animation={animation}
                    variant="text"
                    width="60%"
                    height={20}
                    sx={{ mb: 0.5 }}
                />
                <Skeleton
                    animation={animation}
                    variant="text"
                    width="80%"
                    height={18}
                />
            </Box>

            {/* Bullet points / lista */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                    <Skeleton animation={animation} variant="circular" width={8} height={8} sx={{ mt: 1 }} />
                    <Box sx={{ flex: 1 }}>
                        <Skeleton animation={animation} variant="text" width="95%" height={20} />
                        <Skeleton animation={animation} variant="text" width="88%" height={20} />
                        <Skeleton animation={animation} variant="text" width="75%" height={20} />
                    </Box>
                </Box>
            </Box>

            {/* Parágrafos de conteúdo */}
            {Array.from({ length: contentParagraphs }).map((_, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                    <Skeleton
                        animation={animation}
                        variant="text"
                        width={`${85 + Math.random() * 15}%`}
                        height={20}
                    />
                    <Skeleton
                        animation={animation}
                        variant="text"
                        width={`${70 + Math.random() * 25}%`}
                        height={20}
                    />
                    {index % 2 === 0 && (
                        <Skeleton
                            animation={animation}
                            variant="text"
                            width={`${50 + Math.random() * 30}%`}
                            height={20}
                        />
                    )}
                </Box>
            ))}

            {/* Feedback */}
            {showFeedback && (
                skeletonFeedBack()
            )}

            {/* Área das Tags */}
            {showTags && (
                <>
                    <Skeleton
                        animation={animation}
                        variant="text"
                        width={90}
                        height={24}
                        sx={{ mb: 2 }}
                    />

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {Array.from({ length: 4 }).map((_, index) => (
                            <Skeleton
                                key={index}
                                animation={animation}
                                variant="rectangular"
                                width={80 + Math.random() * 40}
                                height={32}
                                sx={{ borderRadius: 16 }}
                            />
                        ))}
                    </Box>
                </>
            )}
        </Box>
    );

    const skeletonFeedBack = () => {
        return (
            <Box className="feedback-container"
                sx={{
                    marginTop: '40px',
                    paddingTop: '20px',
                    marginBottom: '20px',
                    borderTop: '1px solid #e0e0e0'
                }}
            >
                <Skeleton variant="text" width="25%" height={24} />

                <Box sx={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                    <Skeleton variant="rectangular" width="64px" height={36} sx={{ borderRadius: 1 }} />
                    <Skeleton variant="rectangular" width="64px" height={36} sx={{ borderRadius: 1 }} />
                </Box>
            </Box>
        )
    }

    if (!loading) return <></>;

    return (
        <Grid
            container
            alignItems={"flex-start"}
            justifyContent={"space-between"}
            gap={1}
            {...mainContainerProps}
        >
            {/* Seções na esquerda */}
            {showSections && (
                <Grid item container sm={2.8} width={"100%"}>
                    {renderSectionsSkeleton()}
                </Grid>
            )}

            {/* Layout principal: Breadcrumbs + Conteúdo */}
            <Grid item container sm={9} width={"100%"}>
                {/* Breadcrumbs */}
                {showBreadcrumbs && (
                    <Grid item sm={12} display="flex" flexDirection="row" mb={2}>
                        {renderBreadcrumbsSkeleton()}
                    </Grid>
                )}

                {/* Conteúdo no centro */}
                <Grid item xs={12}>
                    {renderContentSkeleton()}
                </Grid>
            </Grid>
        </Grid>
    );
};

export default ArticleMainContentSkeleton;
